import type { Currency } from '../../constants';
import type { ProgressBarResponse } from '../../model';
import { formatCurrency } from '../currency';

const formatDiscountReward = (tier: ProgressBarResponse['discountTiers'][number], currency: Currency): string => {
  if (tier.discountType === 'fixed_cart') {
    return `${formatCurrency(currency, tier.discountAmount)} off`;
  }

  return `${tier.percentage}% off`;
};

/**
 * Returns the authenticated-user milestone banner message keyed off the cart's
 * current progress through the discount tiers.
 *
 * Bold markers (**text**) are used to indicate semibold text — use parseMessageWithBold()
 * to render them.
 */
export function getAuthenticatedMilestoneBannerMessage(params: {
  progressBar: ProgressBarResponse | null;
  currency: Currency;
}): string | null {
  const { progressBar, currency } = params;
  if (!progressBar) {
    return null;
  }

  const discountTiers = [...progressBar.discountTiers].sort((a, b) => a.displayOrder - b.displayOrder);
  if (discountTiers.length === 0 && !progressBar.freeShipping) {
    return null;
  }

  const freeShipping = progressBar.freeShipping;
  const nextDiscountUnlock = discountTiers.find((t) => !t.available);
  const highestAvailable = [...discountTiers].reverse().find((t) => t.available);

  // Free Shipping is the lowest milestone, so a pending free-shipping tier is
  // always the closest unachieved one — surface it first.
  if (freeShipping && !freeShipping.available) {
    return `Add ${formatCurrency(currency, freeShipping.remaining)} more to unlock **Free Shipping**.`;
  }

  if (!nextDiscountUnlock) {
    if (!highestAvailable) {
      return null;
    }

    return `**${formatDiscountReward(highestAvailable, currency)}** unlocked — plus **free shipping** on top!`;
  }

  const formatted = formatCurrency(currency, nextDiscountUnlock.remaining);
  if (!highestAvailable) {
    return `Add ${formatted} more to unlock **${formatDiscountReward(nextDiscountUnlock, currency)}**.`;
  }

  return `You're getting **${formatDiscountReward(highestAvailable, currency)}** — **add ${formatted}** more for **${formatDiscountReward(nextDiscountUnlock, currency)}**!`;
}
